import { useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateTrackerTags } from "@/api/tracker";
import useNotif from "@/hooks/use-notif";

/**
 * Hook léger pour gérer la mise à jour des tags d'un tracker
 * Utilise les tags passés en props au lieu de les fetcher
 * @param trackerId - L'ID du tracker
 * @param initialTags - Les tags actuels du tracker (depuis trackersTagsMap)
 * @param onTagsUpdated - Callback appelé après mise à jour réussie des tags
 */
export const useTrackerTagsManager = (
  trackerId: string,
  initialTags: string[],
  onTagsUpdated?: (trackerId: string, tags: string[]) => void
) => {
  const { addError, addSuccess } = useNotif();
  const [isLoading, setIsLoading] = useState(false);
  const updateTrackerTagsMutation = useUpdateTrackerTags();
  const queryClient = useQueryClient();

  const updateTags = useCallback(
    async (tagNames: string[]) => {
      setIsLoading(true);
      try {
        await updateTrackerTagsMutation.mutateAsync({
          uriParams: { id: trackerId },
          tags: tagNames,
        });
        addSuccess("Tags mis à jour avec succès");

        if (onTagsUpdated) {
          onTagsUpdated(trackerId, tagNames);
        }

        // Invalidate tags query so the filter dropdown picks up new tags
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return typeof key === "string" && key.includes("tags/find");
          },
        });
      } catch (error) {
        addError("Erreur lors de la mise à jour des tags");
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [trackerId, updateTrackerTagsMutation, addSuccess, addError, onTagsUpdated, queryClient]
  );

  // Ajouter un tag au tracker
  const addTag = useCallback(
    async (tagName: string) => {
      // Vérifier si le tag est déjà associé
      if (initialTags.includes(tagName)) {
        return; // Le tag est déjà associé
      }

      // Ajouter le nouveau tag à la liste existante
      const newTagNames = [...initialTags, tagName];
      await updateTags(newTagNames);
    },
    [initialTags, updateTags]
  );

  // Retirer un tag du tracker
  const removeTag = useCallback(
    async (tagName: string) => {
      // Retirer le tag de la liste existante
      const newTagNames = initialTags.filter((name) => name !== tagName);
      await updateTags(newTagNames);
    },
    [initialTags, updateTags]
  );

  // Toggle un tag (ajouter s'il n'est pas présent, retirer s'il est présent)
  const toggleTag = useCallback(
    async (tagName: string) => {
      const isTagPresent = initialTags.includes(tagName);
      if (isTagPresent) {
        await removeTag(tagName);
      } else {
        await addTag(tagName);
      }
    },
    [initialTags, addTag, removeTag]
  );

  return {
    isLoading,
    addTag,
    removeTag,
    toggleTag,
    updateTags,
  };
};












